/* eslint-disable no-continue */
/* eslint-disable no-restricted-syntax */
import { CommonActions } from '@bugbug/core/actions/actions';
import * as T from '@bugbug/core/utils/toolbox';
import { createSlice, isAnyOf } from '@reduxjs/toolkit';

import type { PendingActionName, SelectedStep } from './uiState.types';
import type { PayloadAction } from '@reduxjs/toolkit';

import type { Group } from '@bugbug/core/types/groups';
import type { IntegrationTrigger } from '@bugbug/core/types/integrations';
import type { Organization } from '@bugbug/core/types/organizations';
import type { PlaybackCursorPosition } from '@bugbug/core/types/playback';
import type { ProjectSettingsTab } from '@bugbug/core/types/projects';
import type { Step } from '@bugbug/core/types/steps';
import type { RunsHistoryTab, Test } from '@bugbug/core/types/tests';
import type { Maybe } from '@bugbug/core/types/utils';
import type { VariablesTab } from '@bugbug/core/types/variables';

import { testsApi } from '../tests/tests.api';
import { BackendActions } from '../websocket/backend.actions';

export interface UIState {
  components: Record<string, object>;
  collapsedGroups: Record<Test['id'], Group['id'][]>;
  draggedStepId: Maybe<Step['id']>;
  draggedStepGroupId: Maybe<Group['id']>;
  ignoreStopTestAlert: boolean;
  playback: {
    cursorPosition: Maybe<PlaybackCursorPosition>;
    selectedPositions: {
      pause: Maybe<{
        afterStepId: Maybe<Step['id']>;
        atGroupId: Group['id'];
      }>;
      recorderStart: Maybe<{
        afterStepId: Maybe<Step['id']>;
        atGroupId: Group['id'];
      }>;
    };
    isDraggingCursor: boolean;
  };
  sessionsIds: Record<Test['id'], string>;
  integrationTrigger?: IntegrationTrigger;
  recentlySelectedTabs: {
    projectSettings: ProjectSettingsTab;
    runsHistory: RunsHistoryTab;
    variables: VariablesTab;
  };
  recentlyAddedStepsIds: Step['id'][];
  pendingAction: Maybe<PendingActionName>;
  lastFocusedStepIdCheckbox: Maybe<SelectedStep>;
  checkedStepsByGroups: Record<Group['id'], Step['id'][]>;
  highlightEditAndRewindMode: boolean;
  stepsClipboard: Record<Organization['id'], Step['id'][]>;
}

export const initialUIState: UIState = {
  components: {},
  collapsedGroups: {},
  recentlySelectedTabs: {
    projectSettings: 'general',
    runsHistory: 'testRunsList',
    variables: 'builtInVariables',
  },
  lastFocusedStepIdCheckbox: null,
  checkedStepsByGroups: {},
  ignoreStopTestAlert: false,
  draggedStepId: null,
  draggedStepGroupId: null,
  playback: {
    cursorPosition: null,
    selectedPositions: {
      pause: null,
      recorderStart: null,
    },
    isDraggingCursor: false,
  },
  recentlyAddedStepsIds: [],
  sessionsIds: {},
  pendingAction: null,
  highlightEditAndRewindMode: false,
  stepsClipboard: {},
};

const uiStateSlice = createSlice({
  name: 'uiState',
  initialState: initialUIState,
  reducers: {
    setUIState(state, action: PayloadAction<{ componentName: string; data: object }>) {
      state.components[action.payload.componentName] = {
        ...state.components[action.payload.componentName],
        ...action.payload.data,
      };
    },

    forgetCollapsedGroup(
      state,
      action: PayloadAction<{ collapsedGroupId: Group['id']; testId: Test['id'] }>,
    ) {
      state.collapsedGroups[action.payload.testId] = T.removeArrayItem(
        state.collapsedGroups[action.payload.testId],
        action.payload.collapsedGroupId,
      );
    },

    setCollapsedGroups(
      state,
      action: PayloadAction<{ collapsedGroupsIds: Group['id'][]; testId: Test['id'] }>,
    ) {
      state.collapsedGroups[action.payload.testId] = action.payload.collapsedGroupsIds;
    },

    toggleCollapsedGroup(
      state,
      action: PayloadAction<{ collapsedGroupId: Group['id']; testId: Test['id'] }>,
    ) {
      state.collapsedGroups[action.payload.testId] = T.toggleArrayItem(
        state.collapsedGroups[action.payload.testId],
        action.payload.collapsedGroupId,
      );
    },

    setIntegrationTrigger(state, action: PayloadAction<UIState['integrationTrigger']>) {
      state.integrationTrigger = action.payload;
    },

    removeIntegrationTrigger(state) {
      delete state.integrationTrigger;
    },

    setProjectSettingsTab(state, action: PayloadAction<ProjectSettingsTab>) {
      state.recentlySelectedTabs.projectSettings = action.payload;
    },

    setRunsHistoryTab(state, action: PayloadAction<RunsHistoryTab>) {
      state.recentlySelectedTabs.runsHistory = action.payload;
    },

    setVariablesTab(state, action: PayloadAction<VariablesTab>) {
      state.recentlySelectedTabs.variables = action.payload;
    },

    setPlaybackDrag(state, action: PayloadAction<{ isDragging: boolean }>) {
      state.playback.isDraggingCursor = action.payload.isDragging;
    },

    setSessionId(state, action: PayloadAction<{ testId: Test['id']; sessionId: string }>) {
      const { testId, sessionId } = action.payload;
      state.sessionsIds[testId] = sessionId;
    },

    addRecentlyAddedStepsIds(state, action: PayloadAction<{ stepsIds: Step['id'][] }>) {
      state.recentlyAddedStepsIds.push(...action.payload.stepsIds);
    },

    resetRecentlyAddedStepsIds(state) {
      state.recentlyAddedStepsIds = [];
    },

    setDraggedStep(
      state,
      action: PayloadAction<{ stepId: Maybe<Step['id']>; groupId: Maybe<Step['groupId']> }>,
    ) {
      state.draggedStepId = action.payload.stepId;
      state.draggedStepGroupId = action.payload.stepId ? action.payload.groupId : null;
    },

    setIgnoreStopTestAlert(state, action: PayloadAction<{ ignore: boolean }>) {
      state.ignoreStopTestAlert = action.payload.ignore;
    },

    disablePlaybackSelectedPositions(state) {
      state.playback.selectedPositions.pause = null;
      state.playback.selectedPositions.recorderStart = null;
    },

    setPendingAction: (state, action: PayloadAction<{ name: UIState['pendingAction'] }>) => {
      state.pendingAction = action.payload.name;
    },

    resetPendingAction: (state) => {
      state.pendingAction = null;
    },

    setCheckedEmptyGroup(state, action: PayloadAction<{ groupId: Group['id']; checked: boolean }>) {
      if (action.payload.checked) {
        state.checkedStepsByGroups[action.payload.groupId] = [];
      } else {
        delete state.checkedStepsByGroups[action.payload.groupId];
      }
    },

    setCheckedSteps(
      state,
      action: PayloadAction<{
        steps: SelectedStep[];
        checked: boolean;
      }>,
    ) {
      const { checked } = action.payload;

      const stepsByGroup = T.groupBy('groupId', action.payload.steps);
      for (const [groupId, steps] of Object.entries(stepsByGroup)) {
        const stepsIds = steps.map((step) => step.id);

        if (checked) {
          state.checkedStepsByGroups[groupId] = T.uniq([
            ...(state.checkedStepsByGroups[groupId] ?? []),
            ...stepsIds,
          ]);
        } else if (state.checkedStepsByGroups[groupId]) {
          state.checkedStepsByGroups[groupId] = state.checkedStepsByGroups[groupId].filter(
            (stepId) => !steps.some((payloadStep) => payloadStep.id === stepId),
          );

          // Remove group if no steps are checked after last uncheck action
          if (!state.checkedStepsByGroups[groupId].length) {
            delete state.checkedStepsByGroups[groupId];
          }
        }
      }
    },

    setLastFocusedStepCheckbox(state, action: PayloadAction<{ step: Maybe<SelectedStep> }>) {
      state.lastFocusedStepIdCheckbox = action.payload.step;
    },

    resetCheckedSteps(state) {
      state.checkedStepsByGroups = {};
    },

    setHighlightEditAndRewindMode(state, action: PayloadAction<{ highlight: boolean }>) {
      state.highlightEditAndRewindMode = action.payload.highlight;
    },

    setStepsClipboard(
      state,
      action: PayloadAction<{ organizationId: Organization['id']; stepsIds: Step['id'][] }>,
    ) {
      state.stepsClipboard[action.payload.organizationId] = action.payload.stepsIds;
    },

    resetStepsClipboard(state, action: PayloadAction<{ organizationId: Organization['id'] }>) {
      delete state.stepsClipboard[action.payload.organizationId];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(BackendActions.testUpdated, (state, action) => {
        const { steps } = action.payload;
        state.recentlyAddedStepsIds.push(...steps.added.map((step) => step.id));

        const latestStep = steps.added[steps.added.length - 1];
        if (latestStep) {
          state.playback.cursorPosition = {
            afterStep: {
              id: latestStep.id,
              groupId: state.playback.cursorPosition?.afterStep?.groupId ?? latestStep.groupId,
            },
            runningStep: null,
            nextStep: null,
          };
        }
      })
      .addCase(CommonActions.setPlaybackCursorPosition, (state, action) => {
        state.playback.cursorPosition = action.payload;
      })
      .addCase(BackendActions.sessionPauseRequested, (state, action) => {
        state.playback.cursorPosition = {
          ...action.payload,
          runningStep: null,
        };
        state.playback.selectedPositions.pause = null;
      })
      .addCase(CommonActions.setPausePosition, (state, action) => {
        state.playback.selectedPositions.pause = action.payload;
      })
      .addCase(CommonActions.setRecordingStartPosition, (state, action) => {
        state.playback.selectedPositions.recorderStart = action.payload;
      })
      .addCase(BackendActions.recordingStarted, (state, action) => {
        const { afterStepId, intoGroupId } = action.payload;
        state.playback.cursorPosition = {
          afterStep: {
            id: afterStepId,
            groupId: intoGroupId,
          },
          runningStep: null,
          nextStep: null,
        };
        state.playback.selectedPositions.recorderStart = null;
      })
      .addMatcher(
        isAnyOf(
          BackendActions.testRunStopped,
          BackendActions.testRunUpdated,
          BackendActions.setRecordingState,
        ),
        (state) => {
          state.pendingAction = null;
        },
      )
      .addMatcher(testsApi.endpoints.updateStepsPosition.matchFulfilled, (state, action) => {
        const { fromGroupId, toGroupId } = action.meta.arg.originalArgs;

        if (fromGroupId !== toGroupId) {
          state.checkedStepsByGroups = {};
        }
      })
      .addMatcher(testsApi.endpoints.splitGroup.matchFulfilled, (state, action) => {
        const { stepsIds } = action.meta.arg.originalArgs;
        const splitGroupsMap = action.payload;

        const changesList = Object.entries(splitGroupsMap);
        if (changesList.length) {
          changesList.forEach(([sourceGroupId, changes]) => {
            const [updatedSourceGroup, createdGroup] = changes;
            const checkedSteps = state.checkedStepsByGroups[updatedSourceGroup.id];
            if (checkedSteps) {
              state.checkedStepsByGroups[sourceGroupId] = checkedSteps.filter(
                (id) => !stepsIds.includes(id),
              );
              if (!state.checkedStepsByGroups[sourceGroupId].length) {
                delete state.checkedStepsByGroups[sourceGroupId];
              }

              const stepsChecksToMove = createdGroup.steps.filter((step) =>
                stepsIds.includes(step.id),
              );
              if (stepsChecksToMove.length) {
                state.checkedStepsByGroups[createdGroup.id] = stepsChecksToMove.map(
                  (step) => step.id,
                );
              }
            }
          });
        }
      });
  },
});

export const UIStateActions = uiStateSlice.actions;

export default uiStateSlice.reducer;
